"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const graphql_1 = require("graphql");
const InvalidArgumentException_1 = tslib_1.__importDefault(require("../models/Exceptions/InvalidArgumentException"));
const Collection_1 = tslib_1.__importDefault(require("../models/Collection"));
const utils_1 = require("../utils");
const UnexpectedException_1 = tslib_1.__importDefault(require("../models/Exceptions/UnexpectedException"));
const ValidationException_1 = tslib_1.__importDefault(require("../models/Exceptions/ValidationException"));
const UnauthorizedException_1 = tslib_1.__importDefault(require("../models/Exceptions/UnauthorizedException"));
const Model_1 = tslib_1.__importDefault(require("../models/Model"));
const EventEmitter_1 = tslib_1.__importDefault(require("../EventEmitter"));
const to_case_1 = tslib_1.__importDefault(require("to-case"));
class Repository extends EventEmitter_1.default {
    constructor(array = null) {
        super();
        this.providerName = 'default';
        this._exists = false;
        this.performSafeRequestGraphql = utils_1.performSafeRequestGraphql;
        this.performSafeRequestREST = utils_1.performSafeRequestREST;
        this.loading = true;
        this.lastError = null;
        this.method = '';
        this.dataset = new Collection_1.default();
        this.paginationInfo = {};
        this.queryParams = {};
        this.model = function undefinedMethod(data) {
            return Object(data);
        };
        if (array instanceof Collection_1.default) {
            this.dataset = array;
        }
        else if (Array.isArray(array)) {
            this.dataset = this.fromArray(array);
        }
        this.emit('construct');
    }
    subscriptions() {
        return [];
    }
    get className() {
        return this.constructor.name;
    }
    get displayClassName() {
        return this.className.toLowerCase();
    }
    get defaultMethod() {
        return utils_1.config(this.providerName).graphql ? 'post' : 'get';
    }
    exists() {
        return this._exists;
    }
    getMethod(_opts) {
        return null;
    }
    fromArray(array, skipEmpty = true) {
        const filtered = array.filter(i => i || !skipEmpty);
        const mapped = filtered.map((i) => {
            let model = this.model;
            if (typeof this.model === 'function' && !utils_1.isClass(this.model)) {
                model = this.model(i);
            }
            return new model(i);
        });
        return new Collection_1.default(mapped);
    }
    static fromArray(array, skipEmpty = true) {
        return (new this).fromArray(array, skipEmpty);
    }
    onError(e) {
        this.lastError = e;
        this.emit('onError');
        if (utils_1.config(this.providerName).graphql && (e instanceof graphql_1.GraphQLError)) {
            const { graphQLErrors: [{ extensions: { errorCode, message } }] = [
                {
                    extensions: {
                        errorCode: 500,
                        message: 'Server Error',
                    },
                },
            ], } = e;
            if (errorCode === 401) {
                throw new UnauthorizedException_1.default(message);
            }
            if (errorCode === 422) {
                throw new ValidationException_1.default(message);
            }
            throw new UnexpectedException_1.default(e.message);
        }
    }
    async query(queryOrUrl, params = {}, responseType, method = this.defaultMethod) {
        if (utils_1.config(this.providerName).graphql) {
            let doc = queryOrUrl;
            if (typeof queryOrUrl === 'function') {
                doc = await queryOrUrl();
            }
            return this.beforeQuery()
                .then(this.performSafeRequestGraphql.bind(this, doc, params, this.providerName))
                .then((data) => (utils_1.isSubscription(data) ? this.processSubscription(data, doc) : this.processResponse(responseType, data)))
                .catch(this.onError.bind(this))
                .finally(this.afterQuery.bind(this));
        }
        this.method = method;
        const resolvedUrl = await utils_1.getUrl({ method, url: queryOrUrl, params });
        const resolvedMethod = await this.getMethod({ method, url: resolvedUrl, params });
        return this.beforeQuery()
            .then(this.performSafeRequestREST.bind(this, resolvedUrl, params || this.queryParams, resolvedMethod || method, null))
            .then(this.processResponse.bind(this, responseType))
            .catch(this.onError.bind(this))
            .finally(this.afterQuery.bind(this));
    }
    processSubscription(observer, doc) {
        const queryName = doc.definitions.map(def => def.name).find(def => def.kind === 'Name').value;
        const that = this;
        observer.subscribe({
            next({ data }) {
                that[`on${to_case_1.default.pascal(queryName)}`](data[queryName]);
            },
            error(err) { console.error('err', err); },
        });
    }
    async beforeQuery() {
        this.loading = true;
        this.emit('beforeQuery');
    }
    async processResponse(responseType, data) {
        let response = data;
        if (responseType === 'collection')
            response = this.fromArray(data);
        if (responseType === 'connection') {
            if (data.nodes) {
                response = this.fromArray(data.nodes);
            }
            else if (data.edges) {
                response = this.fromArray(data.edges.map(edge => edge.node));
            }
            this.paginationInfo.pageInfo = data.pageInfo;
            this.paginationInfo.totalCount = data.totalCount;
        }
        return response;
    }
    async afterQuery() {
        this.loading = false;
        this.emit('afterQuery');
    }
    async many(params = null, responseType = 'collection') {
        if (!this.fetchManyQuery) {
            throw new InvalidArgumentException_1.default('fetchAllQuery is not set for this repository.');
        }
        this.dataset = await this.query(this.fetchManyQuery, params || this.queryParams, responseType);
        this._exists = true;
        return this.dataset;
    }
    async one(id) {
        const identifier = (new this.model()).identifier;
        const params = { [identifier]: id };
        const data = await this.query(this.fetchOneQuery, params);
        let ModelFactory = this.model;
        if (typeof ModelFactory === 'function' && !(ModelFactory.prototype instanceof Model_1.default)) {
            ModelFactory = ModelFactory(data);
        }
        return new ModelFactory(data);
    }
    async subscribeToMore(params = null) {
        if (!this.subscriptions()) {
            throw new InvalidArgumentException_1.default('There are no subscriptions specified for this repository.');
        }
        for (const subscription of this.subscriptions()) {
            await this.query(subscription, params);
        }
    }
    static one(params) {
        return Reflect.construct(this, []).one(params);
    }
}
exports.default = Repository;
//# sourceMappingURL=Repository.js.map